import type { Position, Portfolio } from "@/types/portfolio"
import type { Stock } from "@/types/stock"

/** Merge a backend Position with enriched Stock data into a fully hydrated Position. */
export function hydratePosition(p: Position, stock: Stock): Position {
	return { ...p, ...stock, shares: p.shares } as Position
}

/** Create a Position from a Stock with the given share count. */
export function stockToPosition(stock: Stock, shares: number): Position {
	return { ...stock, id: crypto.randomUUID(), shares } as Position
}

/** Extract Stock fields from a hydrated Position (safe defaults for optional fields). */
export function positionToStock(p: Position): Stock {
	return {
		ticker: p.ticker,
		name: p.name ?? p.ticker,
		exchange: p.exchange,
		price: p.price ?? 0,
		marketCap: p.marketCap ?? "0",
		sector: p.sector ?? "Unknown",
		dividendPerShare: p.dividendPerShare ?? 0,
		dividendYield: p.dividendYield ?? 0,
		dividendGrowth5y: p.dividendGrowth5y ?? 0,
		frequency: p.frequency ?? "Quarterly",
		currency: p.currency,
		safety_score: p.safety_score,
		safety_rating: p.safety_rating,
	}
}

export function isEffectiveDripEnabled(
	position: Pick<Position, "drip_setting">,
	portfolio: Pick<Portfolio, "drip_default"> | undefined,
): boolean {
	return position.drip_setting === "ON" || (position.drip_setting !== "OFF" && portfolio?.drip_default === "ON")
}

const ADJECTIVES = [
	"Golden",
	"Busy",
	"Sweet",
	"Royal",
	"Worker",
	"Drunken",
	"Wild",
	"Stingy",
	"Zesty",
	"Mellow",
	"Chaotic",
	"Greedy",
	"Sleepy",
	"Hyperactive",
	"Grumpy",
	"Fancy",
	"Legendary",
	"Spicy",
	"Clumsy",
	"Snobby",
]
const NOUNS = [
	"Honeycomb",
	"Nectar",
	"Hive",
	"Swarm",
	"Colony",
	"Queen",
	"Beehive",
	"Wax",
	"Pollen",
	"Sting",
	"Money Bin",
	"Dividend Buffet",
	"Gold Mine",
	"Cash Cow",
	"Fortune Nest",
	"Wealth Hive",
	"Profit Palace",
	"Jackpot Junction",
	"Loot Box",
	"Treasure Chest",
]

export function getRandomPortfolioName() {
	const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)]
	const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)]
	return `${adj} ${noun}`
}
